import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import toast from 'react-hot-toast';
import { FiCheckCircle } from 'react-icons/fi';
import {
  SalaryDistributionSummary,
  PaymentEntry,
} from '../service/paymentService';
import jsPDF from 'jspdf';

const EditableInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    background: ${theme.colors.light.background.secondary};
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const SummaryCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[3]};
  text-align: center;
`;

const SummaryValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
`;

const SummaryLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
`;

const SearchContainer = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const SearchInputContainer = styled.div`
  flex: 1;
  min-width: 250px;
`;

const TableWrapper = styled.div`
  width: 100%;
  height: calc(100vh - 500px);
  min-height: 400px;
  max-height: calc(100vh - 500px);
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  
  table {
    table-layout: auto;
    width: 100%;
    min-width: 100%;
  }
  
  thead {
    position: sticky;
    top: 0;
    z-index: 10;
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
  }
  
  th, td {
    text-align: center;
    white-space: nowrap;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
  }
  
  td:first-child {
    text-align: left;
    white-space: normal;
  }
  
  @media (min-width: 1200px) {
    table {
      table-layout: fixed;
    }
    
    th:nth-child(1), td:nth-child(1) { width: 20%; } /* Guard */
    th:nth-child(2), td:nth-child(2) { width: 12%; } /* Advance */
    th:nth-child(3), td:nth-child(3) { width: 12%; } /* Balance Before */
    th:nth-child(4), td:nth-child(4) { width: 12%; } /* Deduction */
    th:nth-child(5), td:nth-child(5) { width: 12%; } /* Amount to Pay */
    th:nth-child(6), td:nth-child(6) { width: 12%; } /* Balance After */
    th:nth-child(7), td:nth-child(7) { width: 20%; } /* Reason */
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-top: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PaginationInfo = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const ReasonInput = styled.textarea`
  width: 100%;
  padding: ${theme.spacing[2]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  font-family: inherit;
  min-height: 60px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const InfoText = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  font-style: italic;
  margin-top: ${theme.spacing[1]};
`;

interface RolloutModalProps {
  isOpen: boolean;
  onClose: () => void;
  salarySummary: SalaryDistributionSummary;
  companyId: string;
  onRollout: (payments: PaymentEntry[]) => Promise<void>;
  initialPayments?: PaymentEntry[];
  isEditMode?: boolean;
  onExportAfterSave?: (payments: PaymentEntry[]) => void;
}

const RolloutModal: React.FC<RolloutModalProps> = ({
  isOpen,
  onClose,
  salarySummary,
  companyId,
  onRollout,
  initialPayments,
  isEditMode = false,
  onExportAfterSave,
}) => {
  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  useEffect(() => {
    if (isOpen && salarySummary) {
      // Use initial payments if provided, otherwise initialize with default values
      if (initialPayments && initialPayments.length > 0) {
        setPayments(initialPayments.map(p => ({
          ...p,
          deductionAmount: p.deductionAmount || (p.balanceBefore - p.amountToPay),
        })));
      } else {
        const defaultPayments: PaymentEntry[] = salarySummary.guards.map((guardInfo) => {
          return {
            guardId: guardInfo.guardId,
            guardName: guardInfo.guard.name || 'Unknown',
            guardIdNumber: guardInfo.guard.guardId,
            amountToPay: guardInfo.currentBalance,
            deductionAmount: 0,
            advanceSalary: guardInfo.advanceSalary,
            balanceBefore: guardInfo.currentBalance,
            balanceAfter: 0,
            months: guardInfo.unpaidMonthKeys || [],
          };
        });
        setPayments(defaultPayments);
      }
    }
  }, [isOpen, salarySummary, initialPayments]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Filter payments based on search term
  const filteredPayments = useMemo(() => {
    if (!searchTerm.trim()) {
      return payments;
    }
    const term = searchTerm.toLowerCase();
    return payments.filter((p) => {
      const name = p.guardName?.toLowerCase() || '';
      const guardId = p.guardIdNumber?.toLowerCase() || '';
      return name.includes(term) || guardId.includes(term);
    });
  }, [searchTerm, payments]);

  // Pagination logic
  const totalPages = Math.ceil(filteredPayments.length / pageSize);
  const paginatedPayments = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredPayments.slice(startIndex, endIndex);
  }, [filteredPayments, currentPage, pageSize]);

  const updatePayment = (index: number, field: keyof PaymentEntry, value: any) => {
    // Create a new array with a new object to ensure React detects the change
    const updated = payments.map((p, i) => i === index ? { ...p } : p);
    
    // Formula:
    // Base Salary (Balance Before) - Deduction = Net Salary
    // Amount to Pay (default when deduction added) = Net Salary
    // Amount to Pay (max) = Net Salary
    // Balance After = Net Salary - Amount to Pay
    
    if (field === 'deductionAmount') {
      // If updating deduction amount
      const deduction = parseFloat(String(value)) || 0;
      const maxDeduction = updated[index].balanceBefore;
      const actualDeduction = Math.min(deduction, maxDeduction);
      const actualDeductionClamped = Math.max(0, actualDeduction); // Can't be negative
      
      // Calculate Net Salary = Base Salary - Deduction
      const netSalary = updated[index].balanceBefore - actualDeductionClamped;
      
      // Store previous values BEFORE updating
      const previousDeduction = updated[index].deductionAmount || 0;
      const currentAmountToPay = updated[index].amountToPay;
      const balanceBefore = updated[index].balanceBefore;
      
      // Update deduction amount
      updated[index].deductionAmount = actualDeductionClamped;
      
      // When deduction is added/changed, automatically set Amount to Pay to Net Salary
      // Only auto-adjust if:
      // 1. Deduction was just added (was 0, now > 0) OR
      // 2. Amount to Pay equals Balance Before (full amount) OR
      // 3. Amount to Pay exceeds Net Salary
      const wasNoDeduction = previousDeduction === 0;
      const isAtFullAmount = Math.abs(currentAmountToPay - balanceBefore) < 1;
      const exceedsNetSalary = currentAmountToPay > netSalary;
      
      if (actualDeductionClamped > 0) {
        if (wasNoDeduction || isAtFullAmount || exceedsNetSalary) {
          // Auto-set Amount to Pay to Net Salary
          updated[index].amountToPay = Math.max(0, netSalary);
        }
      } else {
        // If deduction is removed (set to 0), restore Amount to Pay to Balance Before
        if (Math.abs(currentAmountToPay - netSalary) < 1) {
          updated[index].amountToPay = balanceBefore;
        }
      }
      
      // Recalculate Balance After = Net Salary - Amount to Pay
      updated[index].balanceAfter = Math.max(0, netSalary - updated[index].amountToPay);
      
      // Clear reason if no deduction and no balance
      if (actualDeductionClamped === 0 && updated[index].balanceAfter === 0) {
        updated[index].balanceReason = undefined;
      }
    } else if (field === 'amountToPay') {
      // If updating amount to pay directly
      const amount = parseFloat(String(value)) || 0;
      const deduction = updated[index].deductionAmount || 0;
      
      // Calculate Net Salary = Base Salary - Deduction
      const netSalary = updated[index].balanceBefore - deduction;
      
      // Amount to Pay cannot exceed Net Salary
      const maxAmount = netSalary;
      const actualAmount = Math.min(amount, maxAmount);
      const actualAmountClamped = Math.max(0, actualAmount); // Can't be negative
      
      updated[index].amountToPay = actualAmountClamped;
      
      // Calculate Balance After = Net Salary - Amount to Pay
      updated[index].balanceAfter = Math.max(0, netSalary - actualAmountClamped);
      
      // Clear reason if no balance and no deduction
      if (updated[index].balanceAfter === 0 && deduction === 0) {
        updated[index].balanceReason = undefined;
      }
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    
    setPayments(updated);
  };

  const updateBalanceReason = (index: number, reason: string) => {
    const updated = [...payments];
    updated[index].balanceReason = reason;
    setPayments(updated);
  };

  const totals = useMemo(() => {
    return {
      totalAmount: payments.reduce((sum, p) => sum + (p.amountToPay || 0), 0),
      totalAdvance: payments.reduce((sum, p) => sum + (p.advanceSalary || 0), 0),
      totalBalance: payments.reduce((sum, p) => sum + (p.balanceAfter || 0), 0),
    };
  }, [payments]);

  const handleSave = async () => {
    // Validate that if there's a deduction or remaining balance, reason must be provided
    const invalidPayments = payments.filter((p) => {
      const hasDeduction = (p.deductionAmount && p.deductionAmount > 0);
      const hasRemainingBalance = p.balanceAfter > 0;
      return (hasDeduction || hasRemainingBalance) && !p.balanceReason?.trim();
    });

    if (invalidPayments.length > 0) {
      const guardIds = invalidPayments.map(p => p.guardIdNumber || p.guardName).join(', ');
      const reasons = [];
      if (invalidPayments.some(p => p.deductionAmount && p.deductionAmount > 0)) {
        reasons.push('deduction');
      }
      if (invalidPayments.some(p => p.balanceAfter > 0)) {
        reasons.push('remaining balance');
      }
      toast.error(`Please provide a reason for ${reasons.join(' or ')}. Missing reason for: ${guardIds}`);
      return;
    }

    setSaving(true);
    try {
      await onRollout(payments);
      // If in edit mode and onExportAfterSave is provided, call it to open export modal
      if (isEditMode && onExportAfterSave) {
        onExportAfterSave(payments);
      }
    } catch (error) {
      // Error already handled in parent
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset to original values - full payment with no deduction
    const defaultPayments: PaymentEntry[] = salarySummary.guards.map((guardInfo) => {
      return {
        guardId: guardInfo.guardId,
        guardName: guardInfo.guard.name || 'Unknown',
        guardIdNumber: guardInfo.guard.guardId,
        amountToPay: guardInfo.currentBalance, // Full balance to pay
        deductionAmount: 0, // No deduction
        advanceSalary: guardInfo.advanceSalary,
        balanceBefore: guardInfo.currentBalance,
        balanceAfter: 0, // Balance cleared after payment
        months: guardInfo.unpaidMonthKeys || [],
      };
    });
    setPayments(defaultPayments);
    setSearchTerm('');
    setCurrentPage(1);
    toast.success('Changes reset to original values');
  };

  const exportRolloutPDF = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPos = 20;

      // Header
      pdf.setFontSize(20);
      pdf.text('Salary Rollout Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      pdf.setFontSize(12);
      const currentDate = new Date();
      pdf.text(`Date: ${currentDate.toLocaleDateString()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Summary
      pdf.setFontSize(14);
      pdf.text('Payment Summary', 14, yPos);
      yPos += 8;

      pdf.setFontSize(10);
      pdf.text(`Total Amount to Pay: Rs. ${totals.totalAmount.toLocaleString()}`, 20, yPos);
      yPos += 6;
      pdf.text(`Total Balance After: Rs. ${totals.totalBalance.toLocaleString()}`, 20, yPos);
      yPos += 10;

      // Table header
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Guard', 14, yPos);
      pdf.text('Amount', 70, yPos);
      pdf.text('Balance', 110, yPos);
      pdf.text('Reason', 150, yPos);
      yPos += 8;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.line(14, yPos, pageWidth - 14, yPos);
      yPos += 5;

      // Table rows - export all payments, not just paginated
      for (const payment of payments) {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.text(payment.guardName, 14, yPos);
        
        // Show amount to pay with deduction and reason in same line
        let amountText = `Rs. ${payment.amountToPay.toLocaleString()}`;
        if (payment.deductionAmount && payment.deductionAmount > 0) {
          const reasonText = payment.balanceReason ? `. Reason: ${payment.balanceReason}` : '';
          amountText += ` (Deduction Rs. ${payment.deductionAmount.toLocaleString()}${reasonText})`;
        }
        pdf.text(amountText, 70, yPos, { maxWidth: 50 });
        
        pdf.text(`Rs. ${payment.balanceAfter.toLocaleString()}`, 110, yPos);
        
        // Show reason separately only if there's remaining balance but no deduction
        if (payment.balanceAfter > 0 && (!payment.deductionAmount || payment.deductionAmount === 0) && payment.balanceReason) {
          pdf.text(payment.balanceReason, 150, yPos, { maxWidth: 50 });
        } else {
          pdf.text('—', 150, yPos);
        }
        yPos += 7;
      }

      pdf.save(`Salary_Rollout_${currentDate.toISOString().split('T')[0]}.pdf`);
      toast.success('Rollout PDF exported successfully');
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Salary Distribution" : "Salary Rollout"}
      size="xl"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', gap: theme.spacing[2] }}>
            <Button variant="secondary" onClick={handleReset} disabled={saving}>
              Reset
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          </div>
          <div style={{ display: 'flex', gap: theme.spacing[2] }}>
            <Button
              variant="secondary"
              onClick={exportRolloutPDF}
              disabled={saving}
            >
              Export Report
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              loading={saving}
            >
              <FiCheckCircle size={16} style={{ marginRight: theme.spacing[1] }} />
              {isEditMode ? 'Save Changes' : 'Rollout Salary'}
            </Button>
          </div>
        </div>
      }
    >
      <div>
        <SummaryCards>
          <SummaryCard>
            <SummaryValue color={theme.colors.primary[600]}>
              Rs. {totals.totalAmount.toLocaleString()}
            </SummaryValue>
            <SummaryLabel>Total Amount</SummaryLabel>
          </SummaryCard>
          <SummaryCard>
            <SummaryValue color={theme.colors.accent.danger}>
              Rs. {totals.totalBalance.toLocaleString()}
            </SummaryValue>
            <SummaryLabel>Total Balance</SummaryLabel>
          </SummaryCard>
        </SummaryCards>

        <SearchContainer>
          <SearchInputContainer>
            <Input
              placeholder="Search by name or guard ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              dark
            />
          </SearchInputContainer>
        </SearchContainer>

        <TableWrapper>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Guard</Table.HeaderCell>
                <Table.HeaderCell>Advance</Table.HeaderCell>
                <Table.HeaderCell>Balance Before</Table.HeaderCell>
                <Table.HeaderCell>Deduction Amount</Table.HeaderCell>
                <Table.HeaderCell>Amount to Pay</Table.HeaderCell>
                <Table.HeaderCell>Balance After</Table.HeaderCell>
                <Table.HeaderCell>Reason (if deduction)</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedPayments.map((payment) => {
                // Find the original index in payments array
                const originalIndex = payments.findIndex(p => p.guardId === payment.guardId);
                return (
                  <Table.Row key={payment.guardId}>
                    <Table.Cell>
                      <div>
                        <div style={{ fontWeight: theme.typography.fontWeight.medium }}>
                          {payment.guardName}
                        </div>
                        <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                          {payment.guardIdNumber || '—'}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      Rs. {payment.advanceSalary.toLocaleString()}
                    </Table.Cell>
                    <Table.Cell>
                      <strong>Rs. {payment.balanceBefore.toLocaleString()}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <EditableInput
                        type="number"
                        value={payment.deductionAmount || 0}
                        onChange={(e) => {
                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          updatePayment(originalIndex, 'deductionAmount', isNaN(val) ? 0 : val);
                        }}
                        onBlur={(e) => {
                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          updatePayment(originalIndex, 'deductionAmount', isNaN(val) ? 0 : val);
                        }}
                        min={0}
                        max={payment.balanceBefore}
                        placeholder="0"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <EditableInput
                        type="number"
                        value={payment.amountToPay}
                        onChange={(e) => {
                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          updatePayment(originalIndex, 'amountToPay', isNaN(val) ? 0 : val);
                        }}
                        min={0}
                        max={payment.balanceBefore - (payment.deductionAmount || 0)}
                        placeholder="0"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <strong style={{ 
                        color: payment.balanceAfter > 0 
                          ? theme.colors.accent.danger 
                          : theme.colors.accent.success 
                      }}>
                        Rs. {payment.balanceAfter.toLocaleString()}
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      {((payment.deductionAmount && payment.deductionAmount > 0) || payment.balanceAfter > 0) ? (
                        <ReasonInput
                          placeholder={
                            (payment.deductionAmount && payment.deductionAmount > 0) && payment.balanceAfter > 0
                              ? "Enter reason for deduction and partial payment..."
                              : payment.balanceAfter > 0
                              ? "Enter reason for partial payment..."
                              : "Enter reason for deduction..."
                          }
                          value={payment.balanceReason || ''}
                          onChange={(e) => updateBalanceReason(originalIndex, e.target.value)}
                          required
                        />
                      ) : (
                        <InfoText>No deduction or remaining balance</InfoText>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </TableWrapper>

        {filteredPayments.length > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {paginatedPayments.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{' '}
              {Math.min(currentPage * pageSize, filteredPayments.length)} of {filteredPayments.length} guards
            </PaginationInfo>
            <PaginationControls>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || saving}
              >
                Previous
              </Button>
              <PaginationInfo>
                Page {currentPage} of {totalPages || 1}
              </PaginationInfo>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages || saving}
              >
                Next
              </Button>
            </PaginationControls>
          </PaginationContainer>
        )}
      </div>
    </Modal>
  );
};

export default RolloutModal;
